import { useEffect, useRef, useState } from 'react';
import { headlineExpAPI } from './api';

const STORAGE_KEY = 'nn-ab-bucket';

/**
 * Stable per-browser bucket id used to deterministically pick a variant.
 * One bucket per browser ⇒ a returning visitor sees the same headline,
 * which is what you want for honest CTR measurement.
 */
function bucketId() {
  try {
    let id = window.localStorage.getItem(STORAGE_KEY);
    if (!id) {
      id = Math.random().toString(36).slice(2, 14);
      window.localStorage.setItem(STORAGE_KEY, id);
    }
    return id;
  } catch (_) {
    return 'anon';
  }
}

function hashCode(s) {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = ((h << 5) - h) + s.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}

/**
 * Resolve which headline variant to render for an article.
 * - Reads /api/articles/{id}/headline-experiment.
 * - Picks a variant based on (bucketId, articleId) — stable across page loads.
 * - Records an impression once per (bucket, article) per session.
 * Returns { headline, variantId, recordClick, isExperiment, loading }.
 */
export default function useHeadlineExperiment(article) {
  const [variantId, setVariantId] = useState(null);
  const [headline, setHeadline] = useState(article?.title || '');
  const [loading, setLoading] = useState(false);
  const impressedRef = useRef(false);

  useEffect(() => {
    if (!article?.id) return;
    setHeadline(article.title || '');
    setLoading(true);
    let cancelled = false;
    headlineExpAPI
      .get(article.id)
      .then((r) => {
        if (cancelled) return;
        const exp = r?.data || {};
        const variants = exp.variants || [];
        if (!exp.is_active || variants.length < 2) {
          setVariantId(null);
          setLoading(false);
          return;
        }
        const seed = `${bucketId()}-${article.id}`;
        const idx = hashCode(seed) % variants.length;
        const chosen = variants[idx];
        setVariantId(chosen.id);
        setHeadline(chosen.headline || article.title);

        const sessionFlag = `nn-ab-impressed-${article.id}`;
        if (!impressedRef.current && !sessionStorage.getItem(sessionFlag)) {
          impressedRef.current = true;
          sessionStorage.setItem(sessionFlag, '1');
          headlineExpAPI
            .recordEvent(article.id, chosen.id, 'impression')
            .catch(() => {});
        }
      })
      .catch(() => setVariantId(null))
      .finally(() => setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [article?.id, article?.title]);

  const recordClick = () => {
    if (!article?.id || !variantId) return;
    headlineExpAPI.recordEvent(article.id, variantId, 'click').catch(() => {});
  };

  return {
    headline,
    variantId,
    recordClick,
    isExperiment: !!variantId,
    loading,
  };
}
